import React from "react";
import Dropzone from "react-dropzone";
import {
  uploadAndRegister,
  stripNumericPrefix,
  buildDefaultTargetDir,
  loadDefaultImage,
  deleteDefaultContents,
  uploadDefaultImageFile,
  parseDefaultImgLink,
} from "./ContentLibraryUtils";
import CircularProgress from "@mui/material/CircularProgress";
import ImageIcon from "@mui/icons-material/Image";
import { DROPZONE_ACCEPT, DROPZONE_MAX_SIZE } from "./ContentLibraryUtils";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
} from "@mui/material";
import { CalendarIcon } from "@mui/x-date-pickers";
import ETVPage from "../tools/ETVPage";
import ETVPopup from "../tools/ETVPopup";
import ETVActionButton from "../tools/ETVActionButton";

import * as ICalUtils from "../tools/ICalUtils.js";

import Autocomplete from "@mui/material/Autocomplete";
import SearchIcon from "@mui/icons-material/Search";

import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";

import { format, parse, startOfWeek, getDay } from "date-fns";
import enUS from "date-fns/locale/en-US";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import * as ETVConstants from "../ETVConstants";
import {
  TYPE_COLORS,
  safeType,
  mapLibraryResponse,
  filterLibrary,
  formatTooltip,
  fetchJsonRobust,
  cleanFilename,
  getGroupIdFromTargetId,
  buildSelectOptions,
  mapPlaylistResponseToSchedules,
  updateIcsStartEnd
} from "../tools/ContentPlannerUtils";

import ICalEventEditor from "../tools/ICalEventEditor";

// -------------------- CALENDAR SETUP --------------------
const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});
const DnDCalendar = withDragAndDrop(Calendar);

// -------------------- COMPONENT --------------------
class ContentPlanner extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      schedules: [],
      visibleStart: null,
      visibleEnd: null,
      view: "week",
      viewDate: new Date(),
      // access + selection
      user_access: undefined,
      accessLoaded: false,
      selected_target_id: "",
      selected_group_id: "",
      selected_device_id: "",
      selected_channel_id: 0,
      // library
      library: [],
      libraryQuery: "",
      libraryLoading: false,
      libraryError: null,
      draggedContent: null,
      // playlist
      playlistLoading: false,
      playlistError: null,
      // dialog
      dialogOpen: false,
      editingEventId: null,
      draftTitle: "",
      draftContent: null,
      selectedEventIcs: "",
      draftIcs: "",
      draftEvent: null,
      parallelLimit: "unlimited",
      parallelLimitMin: 1,
      uploadSlotOpen: false,
      uploadSlotStart: null,
      uploadSlotEnd: null,
      uploadProgressOpen: false,
      uploadProgressMsg: "",
      error: "",
      uploadError:"",
      // default image (per channel)
      defaultImageOpen: false,
      default_img_link: undefined,
      uploading_default_img: false,
      defaultImageError: "",
      editorIsValid: true,
      draftIsValid: true,
      draftValidationMsg: "",
    };
  }

  _playlistReqSeq = 0;

  componentDidMount() {
    this.loadLibrary();
    this.loadUserAccess();
  }

  componentDidUpdate(prevProps, prevState) {
    const targetChanged = prevState.selected_target_id !== this.state.selected_target_id;
    const channelChanged = prevState.selected_channel_id !== this.state.selected_channel_id;
    if (targetChanged || channelChanged) {
      this.loadPlaylist();
    }
  }

  // -------------------- ACCESS / SELECTORS --------------------
  loadUserAccess = async () => {
    const accessToken = localStorage.getItem("etv.access_token");
    const reqUserID = localStorage.getItem("etv.user_id");
    const url = `${ETVConstants.getServerBase()}/device_mgmt?action=list&owner_id=${this.props.user_id}`;

    try {
      const result = await fetchJsonRobust(url, { user_id: reqUserID, access_token: accessToken });
      this.setState({ user_access: result, accessLoaded: true }, this.ensureDefaultTargetSelected);
    } catch {
      this.setState({ user_access: null, accessLoaded: true });
    }
  };

//////////////////////////////////////////////////////////////////////////////////////////////////////////  
// default image
//////////////////////////////////////////////////////////////////////////////////////////////////////////  

  // -------------------- DEFAULT IMAGE (per channel) --------------------
  _canUseDefaultImage = () => {
    // backend contract you showed uses device_id (not group_id)
    const { group_id, device_id } = this.getSelectedDeviceOrGroup();
    return !!device_id && group_id == null;
  };

  _getVisibleRangeFromView = () => {
    const { view, viewDate } = this.state;
    const d = viewDate ? new Date(viewDate) : new Date();

    // week starts Monday
    const startOfWeekMon = (x) => {
      const t = new Date(x);
      const day = (t.getDay() + 6) % 7; // Mon=0
      t.setDate(t.getDate() - day);
      t.setHours(0, 0, 0, 0);
      return t;
    };

    if (view === "day") {
      const s = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
      const e = new Date(s.getTime() + 24 * 3600 * 1000);
      return { start: s, end: e };
    }

    if (view === "month") {
      const s = startOfWeekMon(new Date(d.getFullYear(), d.getMonth(), 1));
      const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      const e = new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1, 0, 0, 0, 0);
      return { start: s, end: e };
    }

    // default: week
    const s = startOfWeekMon(d);
    const e = new Date(s.getTime() + 7 * 24 * 3600 * 1000);
    return { start: s, end: e };
  };  

  openDefaultImageDialog = () => {
    this.setState(
      { defaultImageOpen: true, defaultImageError: "", default_img_link: undefined },
      () => this.refreshDefaultImage()
    );
  };

  closeDefaultImageDialog = () => {
    this.setState({ defaultImageOpen: false, defaultImageError: "", uploading_default_img: false });
  };

  refreshDefaultImage = () => {
    const { group_id, device_id } = this.getSelectedDeviceOrGroup();
    const channel = Number(this.state.selected_channel_id);

    if (group_id != null || !device_id) {
      this.setState({ default_img_link: undefined });
      return;
    }

    loadDefaultImage(ETVConstants, { deviceId: device_id, channel }, (jsonObj) => {
      this.setState({ default_img_link: parseDefaultImgLink(jsonObj) });
    });
  };

  deleteDefaultImage = () => {
    const { group_id, device_id } = this.getSelectedDeviceOrGroup();
    const channel = Number(this.state.selected_channel_id);

    if (group_id != null || !device_id) return;

    deleteDefaultContents(ETVConstants, { deviceId: device_id, channel }, (jsonObj) => {
      this.setState({ default_img_link: parseDefaultImgLink(jsonObj) });
    });
  };

  onDropDefaultImage = async (files) => {
    if (!files || files.length === 0) return;

    const { group_id, device_id } = this.getSelectedDeviceOrGroup();
    const channel = Number(this.state.selected_channel_id);

    if (group_id != null || !device_id) {
      this.setState({ defaultImageError: "default image is only available for single devices (not groups)." });
      return;
    }

    const file = files[0];
    const targetDir = buildDefaultTargetDir(device_id, channel);

    this.setState({ uploading_default_img: true, defaultImageError: "" });

    await uploadDefaultImageFile({
      ETVConstants,
      file,
      targetDir,
      onDone: () => {
        this.setState({ uploading_default_img: false });
        this.refreshDefaultImage();
      },
      onError: (e) => {
        this.setState({
          uploading_default_img: false,
          defaultImageError: `upload failed: ${e?.message || e}`,
        });
      },
    });
  };

//////////////////////////////////////////////////////////////////////////////////////////////////////////  
// Uploading
//////////////////////////////////////////////////////////////////////////////////////////////////////////  

_showUploadProgress = (msg) => this.setState({ uploadProgressOpen: true, uploadProgressMsg: msg });
_hideUploadProgress = () => this.setState({ uploadProgressOpen: false, uploadProgressMsg: "" });
_getMB = (len) => `${Math.round((len / 1024 / 1024) * 100) / 100}MB`;


onDropUploadToSlot = async (acceptedFiles, fileRejections) => {
  // Rejects
  if (fileRejections?.length) {
    console.warn("[planner upload] rejected:", fileRejections);
    this.setState({
      uploadError: "file rejected (type/size). please upload a supported file type (max 350mb).",
    });
    return;
  }
  if (!acceptedFiles?.length) return;
  const file0 = acceptedFiles[0];
  const slotStart = this.state.uploadSlotStart;
  const slotEnd = this.state.uploadSlotEnd;
  if (!slotStart || !slotEnd) {
    this.setState({ error: "missing slot time (please click a slot again)" });
    return;
  }

  this.setState({ error: "" });
  this._showUploadProgress(`preparing upload… (${this._getMB(file0.size)})`);

  try {
    const serverBase = ETVConstants.getServerBase();
    const authHeaders = {
      user_id: localStorage.getItem("etv.user_id"),
      access_token: localStorage.getItem("etv.access_token"),
    };

    // --- Upload + register in one call (GCS OR on-prem) ---
    // Needs in your file:
    // import { uploadAndRegister, stripNumericPrefix } from "../tools/ContentLibraryUtils";
    const reg = await uploadAndRegister({
      serverBase,
      authHeaders,
      userID: this.props.user_id,
      file: file0,
      virtualDir: "/", // planner doesn’t expose dirs (yet)
      useOnPrem: !!ETVConstants.ON_PREMISE_VERSION,

      // keep these true if you want same behavior as ContentLibrary:
      enableHeicConversion: true,
      enableMovConversion: true,

      onStatus: (msg) => this._showUploadProgress(msg),
      onProgress: (pct) => this._showUploadProgress(`uploading file (${pct}%)…`),
    });

    const contentId = reg?.content_id;
    if (!contentId) throw new Error("missing content_id after upload/register");

    // Pick a nice title for schedule:
    // Prefer cleanFilename (your planner helper) else strip numeric prefix (utils) else fallback
    const storedFilename = reg?.filename || file0.name;
    const cleanFromUtils = typeof stripNumericPrefix === "function" ? stripNumericPrefix(storedFilename) : storedFilename;
    const cleanFromPlanner = typeof cleanFilename === "function" ? cleanFilename(storedFilename) : cleanFromUtils;
    const title = cleanFromPlanner?.trim() ? cleanFromPlanner.trim() : cleanFromUtils;

    const thumbnail = reg?.thumbnail_url || null;

    // Build ICS and add to playlist
    const { group_id, device_id } = this.getSelectedDeviceOrGroup();
    const channel = Number(this.state.selected_channel_id);

    const ics = ICalUtils.buildInitialIcsFromDrop({
      title,
      start: new Date(slotStart),
      end: new Date(slotEnd),
    });

    // validate parallel before saving
    const temp = {
      id: `__tmp__${Date.now()}`,
      title,
      contentId: String(contentId),
      contentType: "other",
      thumbnail,
      ics,
    };
    this._validateParallelChangeOrThrow([...(this.state.schedules || []), temp], slotStart);

    this._showUploadProgress("adding to playlist…");
    await this.addToChannel(contentId, channel, device_id, group_id != null ? group_id : -1, ics);

    await this.loadPlaylist();
    await this.loadLibrary(); 
    this.setState({ uploadSlotOpen: false });
  } catch (e) {
    console.error("[planner upload] failed:", e);
    this.setState({ uploadError: `upload failed: ${e?.message || e}` });
  } finally {
    this._hideUploadProgress();
  }
};


//////////////////////////////////////////////////////////////////////////////////////////////////////////  
//
//////////////////////////////////////////////////////////////////////////////////////////////////////////  


  handleNavigate = (date) =>
    this.setState({ viewDate: date }, () => {
      const vr = this._getVisibleRangeFromView();
      this.setState({ visibleStart: vr.start, visibleEnd: vr.end }, () => this._ensureParallelLimitInitialized());
  });

  handleView = (view) =>
    this.setState({ view }, () => {
      const vr = this._getVisibleRangeFromView();
      this.setState({ visibleStart: vr.start, visibleEnd: vr.end }, () => this._ensureParallelLimitInitialized());
  });

  ensureDefaultTargetSelected = () => {
    if (this.state.selected_target_id) return;

    const options = buildSelectOptions(this.state.user_access);
    if (!options.length) return;

    const firstDevice = options.find((o) => !String(o.id).startsWith("_group_"));
    const pick = (firstDevice || options[0]).id;

    const gid = getGroupIdFromTargetId(pick);
    this.setState(
      {
        selected_target_id: pick,
        selected_group_id: gid >= 0 ? String(gid) : "",
        selected_device_id: gid >= 0 ? "" : pick,
      },
      () => this.loadPlaylist()
    );
  };

  // -------------------- PARALLEL VALIDATION --------------------
  _getValidationRange = (refDate) => {
    const { visibleStart, visibleEnd } = this.state;
    if (visibleStart && visibleEnd) return { start: visibleStart, end: visibleEnd };

    const d = refDate ? new Date(refDate) : new Date();
    const start = new Date(d);
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7)); // Monday
    start.setHours(0, 0, 0, 0);

    const end = new Date(start.getTime() + 7 * 24 * 3600 * 1000);
    return { start, end };
  };

  _computeMaxParallel = (events) => {
    const points = [];
    for (const ev of events || []) {
      const s = +new Date(ev.start);
      const e = +new Date(ev.end);
      if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) continue;
      points.push([s, +1]);
      points.push([e, -1]);
    }
    points.sort((a, b) => (a[0] - b[0]) || (a[1] - b[1]));

    let cur = 0;
    let max = 0;
    for (const [, delta] of points) {
      cur += delta;
      if (cur > max) max = cur;
    }
    return max;
  };

  _getDisplayEventsFor = (schedules, rangeStart, rangeEnd) => {
    const out = [];
    for (const s of schedules || []) {
      out.push(...ICalUtils.expandSchedulesForRange(s, rangeStart, rangeEnd));
    }
    return out;
  };

  _ensureParallelLimitInitialized = () => {
    const { parallelLimit, parallelLimitMin } = this.state;
    const { start, end } = this._getValidationRange(this.state.viewDate);
    const display = this._getDisplayEventsFor(this.state.schedules, start, end);
    const currentMax = this._computeMaxParallel(display);

    const newMin = Math.max(parallelLimitMin || 1, currentMax || 1);

    let newLimit = parallelLimit;
    if (newLimit === "unlimited") {
      // keep
    } else if (!newLimit) {
      newLimit = String(newMin);
    } else {
      const num = parseInt(String(newLimit), 10);
      newLimit = String(Math.max(Number.isFinite(num) ? num : newMin, newMin));
    }
    if (newMin !== parallelLimitMin || newLimit !== parallelLimit) {
      this.setState({ parallelLimitMin: newMin, parallelLimit: newLimit });
    }
  };

  _validateParallelChangeOrThrow = (nextSchedules, refDateForRange) => {
    const { parallelLimit } = this.state;
    if (parallelLimit === "unlimited") return;

    const limit = Number(parallelLimit || 0);
    if (!limit) return;

    const { start, end } = this._getValidationRange(refDateForRange);
    const display = this._getDisplayEventsFor(nextSchedules, start, end);
    const maxNow = this._computeMaxParallel(display);

    if (maxNow > limit) {
      throw new Error(`max parallel items exceeded: ${maxNow} > ${limit}`);
    }
  };

  // -------------------- TARGET --------------------
  handleTargetChange = (e, value) => {
    if (!value) return;

    const selectedId = value.id;
    const gid = getGroupIdFromTargetId(selectedId);
    const isGroup = gid >= 0;

    this.setState({
      selected_target_id: selectedId,
      selected_group_id: isGroup ? String(gid) : "",
      selected_device_id: isGroup ? "" : selectedId,
    });
  };

  handleChannelChange = (newChannel) => {
    this.setState({ selected_channel_id: Number(newChannel) });
  };

  getSelectedDeviceOrGroup = () => {
    const { selected_target_id, selected_group_id, selected_device_id } = this.state;

    if (selected_group_id && Number(selected_group_id) >= 0) {
      return { group_id: Number(selected_group_id), device_id: null };
    }
    if (selected_device_id) return { group_id: null, device_id: selected_device_id };

    if (String(selected_target_id || "").startsWith("_group_")) {
      const gid = getGroupIdFromTargetId(selected_target_id);
      return { group_id: gid >= 0 ? gid : null, device_id: null };
    }
    return { group_id: null, device_id: selected_target_id || null };
  };

  _hasValidTargetSelected = () => {
    const { group_id, device_id } = this.getSelectedDeviceOrGroup();
    return group_id != null || !!device_id;
  };

  // -------------------- PLAYLIST --------------------
  loadPlaylist = async () => {
    const reqSeq = ++this._playlistReqSeq;
    const accessToken = localStorage.getItem("etv.access_token");
    const reqUserID = localStorage.getItem("etv.user_id");
    const { selected_channel_id } = this.state;
    const { group_id, device_id } = this.getSelectedDeviceOrGroup();
    if (group_id == null && !device_id) return;
    let url =
      `${ETVConstants.getServerBase()}/playlist?` +
      `channel=${encodeURIComponent(selected_channel_id)}` +
      `&user_id=${encodeURIComponent(this.props.user_id)}` +
      `&_ts=${Date.now()}`;
    if (group_id != null) url += `&group_id=${encodeURIComponent(group_id)}`;
    else url += `&device_id=${encodeURIComponent(device_id)}`;

    this.setState({ playlistLoading: true, playlistError: null });
    try {
      const res = await fetch(url, {
        headers: { user_id: reqUserID, access_token: accessToken },
        cache: "no-store",
      });
      const result = await res.json();
      if (reqSeq !== this._playlistReqSeq) return;
      const schedules = mapPlaylistResponseToSchedules(result, cleanFilename);
      const vr = this._getVisibleRangeFromView();
      this.setState({ schedules, playlistLoading: false, playlistError: null, visibleStart: vr.start, visibleEnd: vr.end, }, () => {
        this._ensureParallelLimitInitialized();
      });

    } catch (e) {
      if (reqSeq !== this._playlistReqSeq) return;
      this.setState({ schedules: [], playlistLoading: false, playlistError: e?.message || String(e) });
    }
  };

  addToChannel = async (contentID, channel, deviceID, groupID, calIcs) => {
    const base = `${ETVConstants.getServerBase()}/playlist`;
    const params = new URLSearchParams();
    params.set("channel", String(channel));
    params.set("content_id", String(contentID));
    params.set("cal_ics", String(calIcs || ""));

    if (Number.isInteger(groupID) && groupID >= 0) {
      params.set("action", "add_to_group_playlist");
      params.set("group_id", String(groupID));
    } else {
      params.set("action", "add_to_playlist");
      params.set("device_id", String(deviceID));
    }

    params.set("_ts", String(Date.now()));
    const url = `${base}?${params.toString()}`;
    await ETVConstants.sendCMDToServerAwait(url);
    this.props.addedToChannelF?.();
  };

  updatePlaylistIcs = async (playlistId, channel, deviceID, groupID, calIcs) => {
    const base = `${ETVConstants.getServerBase()}/playlist`;
    const params = new URLSearchParams();
    params.set("action", "set_cal_ics");
    params.set("playlist_id", String(playlistId));
    params.set("cal_ics", String(calIcs || ""));
    params.set("channel", String(channel));
    if (Number.isInteger(groupID) && groupID >= 0) params.set("group_id", String(groupID));
    else params.set("device_id", String(deviceID || ""));
    params.set("_ts", String(Date.now()));

    const url = `${base}?${params.toString()}`;
    await ETVConstants.sendCMDToServer(url);
  };

  // -------------------- LIBRARY --------------------
  loadLibrary = async () => {
    const accessToken = localStorage.getItem("etv.access_token");
    const reqUserID = localStorage.getItem("etv.user_id");

    const url = `${ETVConstants.getServerBase()}/content_library?action=list&user_id=${this.props.user_id}`;

    this.setState({ libraryLoading: true, libraryError: null });

    try {
      const result = await fetchJsonRobust(url, { user_id: reqUserID, access_token: accessToken });
      const mapped = mapLibraryResponse(result);
      this.setState({ library: mapped, libraryLoading: false });
    } catch (err) {
      this.setState({ libraryLoading: false, libraryError: err?.message || String(err) });
    }
  };

  // -------------------- DRAG --------------------
  _draggedContentRef = null;

  setDraggedContent = (item) => {
    this._draggedContentRef = item;
    this.setState({ draggedContent: item });
  };

  clearDraggedContent = () =>
    window.setTimeout(() => {
      this._draggedContentRef = null;
      this.setState({ draggedContent: null });
    }, 0);

  dragFromOutsideItem = () => {
    const item = this._draggedContentRef || this.state.draggedContent;
    if (!item) return null;

    const start = new Date();
    const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);

    return {
      id: `drag_${item.id}`,
      title: item.filename || item.name || "content",
      start,
      end,
      allDay: false,
      resource: item,
    };
  };

  onDropFromOutside = async ({ start, end }) => {
    const item = this._draggedContentRef || this.state.draggedContent;
    if (!item) return;

    const { group_id, device_id } = this.getSelectedDeviceOrGroup();
    const channel = Number(this.state.selected_channel_id);

    if (group_id == null && !device_id) {
      this.setState({ error: "please select a device or group first" });
      this.clearDraggedContent();
      return;
    }

    let s = ICalUtils.floorTo15(new Date(start));
    let e = ICalUtils.floorTo15(new Date(end));
    if (e <= s) e = new Date(s.getTime() + 15 * 60 * 1000);
    e = ICalUtils.clampEndSameDay(s, e);

    const title = item.filename || item.name || "schedule";

    const ics = ICalUtils.buildInitialIcsFromDrop({
      title,
      start: s,
      end: e,
    });

    const tempId = `__tmp__${Date.now()}`;

    const temp = {
      id: tempId,
      playlistId: tempId,
      title,
      contentId: String(item.id),
      contentType: item.type || item.contentType || "other",
      thumbnail: item.thumbnail || null,
      channel,
      device_id,
      group_id,
      ics,
    };

    try {
      this._validateParallelChangeOrThrow([...(this.state.schedules || []), temp], s);

      // show immediately
      this.setState((prev) => ({
        schedules: [...(prev.schedules || []), temp],
        error: "",
      }));

      await this.addToChannel(item.id, channel, device_id, group_id != null ? group_id : -1, ics);

      // reload in background to replace temp item with real backend item
      await this.loadPlaylist();
    } catch (e) {
      this.setState((prev) => ({
        schedules: (prev.schedules || []).filter((x) => x.id !== tempId),
        error: `failed to add: ${e?.message || e}`,
      }));
      await this.loadPlaylist();
    } finally {
      this.clearDraggedContent();
    }
  };

////////////////////////////////////////////////////////
  draggableAccessor = () => true;
  resizableAccessor = () => true;

  _lastGestureTs = 0;
  _markGesture = () => {
    this._lastGestureTs = Date.now();
  };
  _shouldIgnoreClicks = () => Date.now() - this._lastGestureTs < 250;

  onDoubleClickEvent = (ev) => {
    if (this._shouldIgnoreClicks()) return;
    const baseId = String(ev.id || "").split("__")[0];
    const base = (this.state.schedules || []).find((x) => String(x.id) === String(baseId));
    if (!base) return;
    this.openDialogEdit(base);
  };

  // -------------------- DIALOG --------------------
  openDialogNew = (content, start, end) => {
    const title = content?.filename || content?.name || "new slot";

    const s = start ? ICalUtils.floorTo15(new Date(start)) : ICalUtils.floorTo15(new Date());
    let e = end ? ICalUtils.floorTo15(new Date(end)) : new Date(s.getTime() + 2 * 60 * 60 * 1000);
    e = ICalUtils.clampEndSameDay(s, e);

    const initialIcs = ICalUtils.buildInitialIcsFromDrop({
      title,
      start: s,
      end: e,
    });

    this.setState({
      dialogOpen: true,
      editingEventId: null,
      draftTitle: title,
      draftContent: {
        ...(content || {}),
        thumbnail: content?.thumbnail || null,
        filename: content?.filename || title,
      },
      selectedEventIcs: initialIcs,
      draftIcs: initialIcs,
      draftEvent: { dtStart: s, dtEnd: e },
      error: "",
    });
  };

  openDialogEdit = (event) => {
    this.setState({
      editorIsValid: true,
      dialogOpen: true,
      editingEventId: String(event.id),
      draftTitle: event.title || "",
      draftContent: event.contentId
        ? {
            id: event.contentId,
            name: event.title,
            type: event.contentType,
            thumbnail: event.thumbnail || null,
            filename: event.title,
          }
        : null,
      selectedEventIcs: event?.ics || "",
      draftIcs: event?.ics || "",
      draftEvent: null,
      error: "",
    });
  };

  onCalendarRangeChange = (range) => {
    let start = null;
    let end = null;
    if (Array.isArray(range) && range.length) {
      start = range[0];
      end = range[range.length - 1];
    } else if (range?.start && range?.end) {
      start = range.start;
      end = range.end;
    }
    if (!start || !end) return;
    this.setState({ visibleStart: new Date(start), visibleEnd: new Date(end) }, () =>
      this._ensureParallelLimitInitialized()
    );
  };

  startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  addDays = (d, n) => {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
  };

  _normalizeEventToMinute = (ev) => {
    const start = new Date(ev.start);
    const end = new Date(ev.end);

    start.setSeconds(0, 0);
    end.setSeconds(0, 0);

    return { ...ev, start, end };
  };

  getDisplayEvents = () => {
    const { schedules, visibleStart, visibleEnd, viewDate } = this.state;
    const base = viewDate ? new Date(viewDate) : new Date();
    let rs = visibleStart ? new Date(visibleStart) : base;
    let re = visibleEnd ? new Date(visibleEnd) : new Date(+base + 7 * 24 * 3600 * 1000);
    rs = this.startOfDay(rs);
    re = this.addDays(this.startOfDay(re), 1);
    const out = [];
    for (const s of schedules || []) {
      out.push(...ICalUtils.expandSchedulesForRange(s, rs, re));
    }
    return out.map(this._normalizeEventToMinute);
  };

  closeDialog = () => {
    this.setState({
      dialogOpen: false,
      editingEventId: null,
      draftContent: null,
      error: "",
      selectedEventIcs: "",
      draftIcs: "",
      draftEvent: null,
    });
  };

  // -------------------- SAVE EVENT --------------------
  addOrUpdateEvent = async () => {
    const { editingEventId, draftContent, draftIcs } = this.state;
    if (!draftIcs) return this.setState({ error: "please define a schedule" });

    const { group_id, device_id } = this.getSelectedDeviceOrGroup();
    const channel = Number(this.state.selected_channel_id);

    this.setState({ dialogOpen: false, editingEventId: null, draftContent: null, error: "" });

    try {
      if (editingEventId) {
        const event = (this.state.schedules || []).find((e) => String(e.id) === String(editingEventId));
        if (!event) throw new Error("schedule not found");

        await this.updatePlaylistIcs(event.playlistId || event.id, event.channel, event.device_id, -1, draftIcs);
      } else {
        const contentId = draftContent?.id;
        if (!contentId) throw new Error("missing content id");
        await this.addToChannel(contentId, channel, device_id, group_id != null ? group_id : -1, draftIcs);
      }
      await this.loadPlaylist();
    } catch (e) {
      this.setState({ error: `failed to save schedule: ${e?.message || e}` });
      await this.loadPlaylist();
    }
  };

  deleteEvent = async () => {
    const { editingEventId, schedules } = this.state;
    if (!editingEventId) return;

    const ev = (schedules || []).find((x) => String(x.id) === String(editingEventId));
    if (!ev) return;

    try {
      const { group_id, device_id } = this.getSelectedDeviceOrGroup();

      await this.removeFromChannelByPlaylistId(
        ev.playlistId || ev.id,
        ev.channel,
        device_id,                        // may be null if group
        group_id != null ? group_id : -1  // ✅ route correctly
      );

      await this.loadPlaylist();
      this.closeDialog();
    } catch (e) {
      this.setState({ error: String(e?.message || e) });
      await this.loadPlaylist();
    }
  };

  removeFromChannelByPlaylistId = async (playlistId, channel, deviceID, groupID) => {
    const accessToken = localStorage.getItem("etv.access_token");
    const reqUserID = localStorage.getItem("etv.user_id");

    const base = `${ETVConstants.getServerBase()}/playlist`;
    const params = new URLSearchParams();
    params.set("action", "remove");
    params.set("channel", String(channel));
    params.set("playlist_id", String(playlistId));
    params.set("_ts", String(Date.now()));

    if (Number.isInteger(groupID) && groupID >= 0) {
      params.set("group_id", String(groupID));
    } else {
      params.set("device_id", String(deviceID || ""));
    }
    const url = `${base}?${params.toString()}`;
    const res = await fetch(url, {
      method: "GET",
      headers: { user_id: reqUserID, access_token: accessToken },
      cache: "no-store",
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  };  

  // -------------------- MOVE / RESIZE (LOCAL-ONLY) --------------------

  updateIcsForMove = (ics, newStartJs, newEndJsIgnored) => {
    // move keeps duration (series-wide)
    const parsed = ICalUtils.parseIcsForExpansion(ics);
    if (!parsed?.dtStart || !parsed?.dtEnd) throw new Error("invalid ics: missing dtstart/dtend");
    const durMs = Math.max(15 * 60 * 1000, parsed.dtEnd.getTime() - parsed.dtStart.getTime());
    const s = new Date(newStartJs);
    let e = new Date(s.getTime() + durMs);
    e = ICalUtils.clampEndSameDay(s, e);
    return updateIcsStartEnd(ics, s, e);
  };

  updateIcsForResize = (ics, newStartJs, newEndJs) => {
    // resize uses exactly user edges (series-wide)
    const s = new Date(newStartJs);
    let e = new Date(newEndJs);
    if (e <= s) e = new Date(s.getTime() + 15 * 60 * 1000);
    e = ICalUtils.clampEndSameDay(s, e);
    return updateIcsStartEnd(ics, s, e);
  };

  onEventDrop = async ({ event, start, end }) => {
    this._markGesture();
    const base = event?.resource;
    if (!base?.ics) return;

    // snap start down; keep duration series-wide
    let s = ICalUtils.floorTo15(new Date(start));

    try {
      const newIcs = this.updateIcsForMove(base.ics, s, end);

      const nextSchedules = (this.state.schedules || []).map((x) =>
        String(x.id) === String(base.id) ? { ...x, ics: newIcs } : x
      );
      this._validateParallelChangeOrThrow(nextSchedules, s);

      this.setState((prev) => ({
        schedules: (prev.schedules || []).map((x) =>
          String(x.id) === String(base.id) ? { ...x, ics: newIcs } : x
        ),
      }));

      await this.updatePlaylistIcs(base.playlistId || base.id, base.channel, base.device_id, -1, newIcs);
      await this.loadPlaylist();
    } catch (err) {
      this.setState({ error: `move rejected: ${err?.message || err}` });
      await this.loadPlaylist();
    }
  };

  onSelectSlot = ({ start }) => {
    if (this._shouldIgnoreClicks()) return;

    const s = ICalUtils.floorTo15(new Date(start));
    let e = new Date(s.getTime() + 2 * 60 * 60 * 1000); // default 2 hours
    e = ICalUtils.clampEndSameDay(s, e);

    this.setState({
      uploadSlotOpen: true,
      uploadSlotStart: s,
      uploadSlotEnd: e,
      error: "",
    });
  };

  onEventResize = async ({ event, start, end }) => {
    this._markGesture();
    const base = event?.resource;
    if (!base?.ics) return;

    const oldStart = new Date(event.start);
    const oldEnd = new Date(event.end);

    const startMoved = +new Date(start) !== +oldStart;
    const endMoved = +new Date(end) !== +oldEnd;

    let s = oldStart;
    let e = oldEnd;

    if (startMoved) s = ICalUtils.roundTo15(new Date(start));
    if (endMoved) e = ICalUtils.roundTo15(new Date(end));
    if (!startMoved && !endMoved) {
      s = ICalUtils.roundTo15(new Date(start));
      e = ICalUtils.roundTo15(new Date(end));
    }

    if (e <= s) e = new Date(s.getTime() + 15 * 60 * 1000);
    e = ICalUtils.clampEndSameDay(s, e);

    try {
      const newIcs = this.updateIcsForResize(base.ics, s, e);

      const nextSchedules = (this.state.schedules || []).map((x) =>
        String(x.id) === String(base.id) ? { ...x, ics: newIcs } : x
      );
      this._validateParallelChangeOrThrow(nextSchedules, s);

      this.setState((prev) => ({
        schedules: (prev.schedules || []).map((x) =>
          String(x.id) === String(base.id) ? { ...x, ics: newIcs } : x
        ),
      }));

      await this.updatePlaylistIcs(base.playlistId || base.id, base.channel, base.device_id, -1, newIcs);
      await this.loadPlaylist();
    } catch (err) {
      this.setState({ error: `resize failed: ${err?.message || err}` });
      await this.loadPlaylist();
    }
  };

  // -------------------- STYLING --------------------
  eventPropGetter = (event) => {
    const baseColor = event.color || TYPE_COLORS[safeType({ type: event.contentType })] || TYPE_COLORS.other;
    return {
      style: {
        background: `linear-gradient(135deg, ${baseColor}, ${baseColor}cc)`,
        borderRadius: 10,
        color: "#fff",
        border: "none",
        padding: "4px 8px",
        fontSize: 12,
        fontWeight: 600,
        boxShadow: "0 3px 6px rgba(0,0,0,0.15)",
        overflow: "hidden",
      },
    };
  };

  EventRenderer = ({ event }) => {
    const isSameDay =
      event.start &&
      event.end &&
      event.start.toDateString() === event.end.toDateString();

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4, minWidth: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 800, color: "#fff", lineHeight: 1.1 }} noWrap>
          {event.title}
        </Typography>

        {isSameDay ? (
          <>
            <Typography
              variant="caption"
              sx={{ fontWeight: 400, opacity: 0.9, color: "#fff", fontSize: 10, lineHeight: 1.1 }}
              noWrap
            >
              {format(event.start, "HH:mm")} - {format(event.end, "HH:mm")}
            </Typography>

            {event.thumbnail ? (
              <Box
                component="img"
                src={event.thumbnail}
                alt=""
                sx={{ width: 28, height: 28, borderRadius: 1, objectFit: "cover", mt: 0.3 }}
              />
            ) : null}
          </>
        ) : null}
      </Box>
    );
  };

  // -------------------- RENDER --------------------
  render() {
    const {
      schedules,
      library,
      libraryQuery,
      libraryLoading,
      libraryError,

      dialogOpen,
      editingEventId,
      draftTitle,
      draftContent,

      error,

      user_access,
      accessLoaded,
      selected_target_id,
      selected_channel_id,
      playlistLoading,
      playlistError,
    } = this.state;

    const filteredLibrary = filterLibrary(library, libraryQuery);
    const combinedOptions = buildSelectOptions(user_access);
    const selectedValue = combinedOptions.find((o) => o.id === selected_target_id) || null;

    return (
      <>
        <ETVPage
          width="wide"
          icon={<CalendarIcon />}
          title={ETVConstants.trans("content_planner")}
          loading={!accessLoaded || libraryLoading || playlistLoading}
        >
          <Stack spacing={1.5}>
            {error ? (
              <Alert severity="error">{error}</Alert>
            ) : playlistError ? (
              <Alert severity="error">{String(playlistError)}</Alert>
            ) : null}

            {/* Main split layout */}
            <Box sx={{ display: "flex", width: "100%", height: "75vh", gap: 2, alignItems: "stretch" }}>
              {/* LEFT: selection and library */}
              <Stack sx={{ width: 320, minWidth: 320, height: "100%" }} spacing={2}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={1.5}>
                    <Autocomplete
                      id="select-group-or-device"
                      options={combinedOptions}
                      groupBy={(option) => option.group_name}
                      getOptionLabel={(option) => option.device_name || ""}
                      value={selectedValue}
                      onChange={this.handleTargetChange}
                      loading={!accessLoaded}
                      disabled={!accessLoaded}
                      fullWidth
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="select device"
                          placeholder="search device or group..."
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />

                    <TextField
                      label="channel"
                      value={selected_channel_id}
                      onChange={(e) => this.handleChannelChange(e.target.value)}
                      select
                      size="small"
                      fullWidth
                      disabled={!selected_target_id}
                    >
                      {[0, 1, 2, 3, 4].map((ch) => (
                        <MenuItem key={ch} value={ch}>
                          Channel {ch}
                        </MenuItem>
                      ))}
                    </TextField>

                    <Button
                      fullWidth
                      size="small"
                      variant="contained"
                      startIcon={<ImageIcon />}
                      onClick={this.openDefaultImageDialog}
                      disabled={!selected_target_id || !this._canUseDefaultImage()}
                    >
                      {ETVConstants.trans("default_image")}
                    </Button>
                  </Stack>
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{ p: 1.5, minHeight: 0, flex: 1, display: "flex", flexDirection: "column", gap: 1.25 }}
                >
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {ETVConstants.trans("content_library")}
                </Typography>

                <TextField
                  size="small"
                  value={libraryQuery}
                  onChange={(e) => this.setState({ libraryQuery: e.target.value })}
                  placeholder={"search content..."}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ overflowY: "auto", pr: 0.5, flex: 1 }}>
                  <Stack spacing={1} key={`lib-${libraryQuery || ""}`}>
                    {libraryLoading ? (
                      <Typography variant="body2" color="text.secondary">
                        {ETVConstants.trans("loading_library")}
                      </Typography>
                    ) : libraryError ? (
                      <Typography variant="body2" color="error">
                        {ETVConstants.trans("failed_to_load_library")}
                      </Typography>
                    ) : filteredLibrary.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        {ETVConstants.trans("no_matching_content")}
                      </Typography>
                    ) : (
                      filteredLibrary.map((item) => {
                        const t = safeType(item);
                        const color = TYPE_COLORS[t] || TYPE_COLORS.other;

                        return (
                          <Paper
                            key={`${item.id}:${item.filename}`}
                            variant="outlined"
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.effectAllowed = "copy";
                              e.dataTransfer.setData("text/plain", String(item.id));
                              this.setDraggedContent(item);
                            }}
                            onDragEnd={() => this.clearDraggedContent()}
                            sx={{
                              p: 1,
                              cursor: this._hasValidTargetSelected() ? "grab" : "not-allowed",
                              opacity: this._hasValidTargetSelected() ? 1 : 0.45,
                              userSelect: "none",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              borderLeft: `6px solid ${color}`,
                              transition: "all 0.15s ease",
                              "&:hover": { backgroundColor: "rgba(0,0,0,0.03)" },
                            }}
                          >
                            {item.thumbnail ? (
                              <Box
                                component="img"
                                src={item.thumbnail}
                                alt=""
                                sx={{ width: 40, height: 40, borderRadius: 1, objectFit: "cover" }}
                              />
                            ) : (
                              <Box sx={{ width: 40, height: 40, borderRadius: 1, backgroundColor: color, opacity: 0.2 }} />
                            )}

                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                {item.filename}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ textTransform: "lowercase" }} noWrap>
                                {t}
                              </Typography>
                            </Box>
                          </Paper>
                        );
                      })
                    )}
                  </Stack>
                </Box>

                <Typography variant="caption" color="text.secondary">
                  {ETVConstants.trans("drag_an_item_onto_the_calendar_to_schedule_it")}
                </Typography>
                </Paper>
              </Stack>

              {/* RIGHT: calendar */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <DndProvider backend={HTML5Backend}>
                  <DnDCalendar
                    eventIdAccessor="id"
                    components={{ event: this.EventRenderer }}
                    localizer={localizer}
                    view={this.state.view}
                    date={this.state.viewDate}
                    onNavigate={this.handleNavigate}
                    onView={this.handleView}
                    onRangeChange={this.onCalendarRangeChange}
                    events={this.getDisplayEvents()}
                    defaultView="week"
                    views={["month", "week", "day"]}
                    dayLayoutAlgorithm="no-overlap"
                    dragFromOutsideItem={this.dragFromOutsideItem}
                    onDropFromOutside={this.onDropFromOutside}
                    onSelectEvent={() => {}}
                    onDoubleClickEvent={this.onDoubleClickEvent}
                    draggableAccessor={this.draggableAccessor}
                    resizableAccessor={this.resizableAccessor}
                    onEventDrop={this.onEventDrop}
                    onEventResize={this.onEventResize}
                    resizable
                    selectable
                    onSelectSlot={this.onSelectSlot}
                    eventPropGetter={this.eventPropGetter}
                    tooltipAccessor={formatTooltip}
                    popup
                    style={{ width: "100%", height: "100%" }}
                    formats={{
                      timeGutterFormat: "HH:mm",
                      eventTimeRangeFormat: () => "",
                    }}
                  />
                </DndProvider>
              </Box>
            </Box>
          </Stack>
        </ETVPage>

        {/* new content dialog  */}
        <Dialog
          open={this.state.uploadSlotOpen}
          onClose={() => this.setState({ uploadSlotOpen: false })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {ETVConstants.trans("upload_new_content_for")} {format(this.state.uploadSlotStart || new Date(), "EEE HH:mm")}
          </DialogTitle>

          <DialogContent dividers>
            <Dropzone
              onDrop={this.onDropUploadToSlot}
              accept={DROPZONE_ACCEPT} maxSize={DROPZONE_MAX_SIZE}
              multiple={false}
              showAlerts={false}
            >
              {({ getRootProps, getInputProps }) => (
                <Box
                  {...getRootProps()}
                  sx={{
                    border: "2px dashed",
                    borderColor: "divider",
                    minHeight: 180,
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    bgcolor: "background.default",
                    cursor: "pointer",
                  }}
                >
                  <input {...getInputProps()} />
                  <Stack spacing={1} alignItems="center">
                    <Typography variant="body2">
                      drag & drop file here, or click to select
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      supported: html, images, mp4/webm/3gp/mov, pdf (max 350mb)
                    </Typography>
                  </Stack>
                </Box>
              )}
            </Dropzone>

            {this.state.uploadError ? (
              <Alert sx={{ mt: 2 }} severity="error">{this.state.uploadError}</Alert>
            ) : null}
          </DialogContent>

          <DialogActions>
            <Button onClick={() => this.setState({ uploadSlotOpen: false })}>cancel</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={this.state.uploadProgressOpen} maxWidth="xs">
          <DialogTitle>uploading…</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2">{this.state.uploadProgressMsg || "working..."}</Typography>
          </DialogContent>
        </Dialog>


        <Dialog
          open={this.state.defaultImageOpen}
          maxWidth="xs"
          fullWidth
          onClose={this.closeDefaultImageDialog}
        >
          <DialogTitle>
            {ETVConstants.trans("default_image")}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {ETVConstants.trans("default_image_explanation")}
            </Typography>
          </DialogTitle>

          <DialogContent dividers>
            {this.state.defaultImageError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {this.state.defaultImageError}
              </Alert>
            ) : null}

            {!this.state.default_img_link && !this.state.uploading_default_img ? (
              <div style={{ width: 200, height: "auto" }}>
                <Dropzone
                  onDrop={(accepted) => this.onDropDefaultImage(accepted)}
                  disabled={false}
                  accept={{ "image/jpeg": [".jpg", ".jpeg"] }}
                  multiple={false}
                  minSize={1024}
                  maxSize={5242880}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div {...getRootProps({ className: "dropzone" })}>
                      <input {...getInputProps()} />
                      <p>{ETVConstants.trans("click_to_upload_files_max_5mb")}</p>
                    </div>
                  )}
                </Dropzone>
              </div>
            ) : null}

            {this.state.uploading_default_img ? (
              <CircularProgress sx={{ mt: 1 }} size={50} />
            ) : null}

            {this.state.default_img_link ? (
              <Box
                component="img"
                sx={{ width: "100%", height: "auto", objectFit: "cover", borderRadius: 2 }}
                src={
                  ETVConstants.ON_PREMISE_VERSION
                    ? ETVConstants.makeContentURLRelative(this.state.default_img_link)
                    : this.state.default_img_link
                }
              />
            ) : null}

            {this.state.default_img_link ? (
              <Button
                onClick={this.deleteDefaultImage}
                sx={{ mt: 1 }}
                size="small"
                variant="contained"
                color="secondary"
              >
                {ETVConstants.trans("delete")}
              </Button>
            ) : null}
          </DialogContent>

          <DialogActions>
            <Button onClick={this.closeDefaultImageDialog}>
              {ETVConstants.trans("close")}
            </Button>
          </DialogActions>
        </Dialog>


        <ETVPopup
          open={dialogOpen}
          onClose={this.closeDialog}
          width="small"
          icon={<CalendarIcon />}
          title={editingEventId ? ETVConstants.trans("edit_schedule") : ETVConstants.trans("create_schedule")}
          bottomActions={
            <>
              {editingEventId ? (
                <ETVActionButton
                  action="delete"
                  label={ETVConstants.trans("delete")}
                  onClick={this.deleteEvent}
                />
              ) : null}
              <Box sx={{ flexGrow: 1 }} />
              <ETVActionButton
                action="cancel"
                label={ETVConstants.trans("cancel")}
                onClick={this.closeDialog}
              />
              <ETVActionButton
                action={editingEventId ? "update" : "save"}
                label={editingEventId ? ETVConstants.trans("update") : ETVConstants.trans("save")}
                onClick={this.addOrUpdateEvent}
                disabled={!this.state.editorIsValid || !this.state.draftIsValid || !this.state.draftIcs}
              />
            </>
          }
        >
          <Stack spacing={2}>
            <ICalEventEditor
              key={`${this.state.editingEventId || "new"}:${String(this.state.selectedEventIcs || "").length}`}
              filename={draftContent?.filename || draftTitle || ""}
              thumbnail={draftContent?.thumbnail || null}
              initialIcs={this.state.selectedEventIcs || ""}
              onChange={({ ics, event, isValid, validationMsg }) =>
                this.setState({
                  draftIcs: ics,
                  draftEvent: event,
                  draftIsValid: isValid !== false,
                  draftValidationMsg: validationMsg || "",
                })
              }
            />
            {error ? <Alert severity="error">{error}</Alert> : null}
          </Stack>
        </ETVPopup>
      </>
    );
  }
}

export default ContentPlanner;
